import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";

import { Row, Col, Form, Button, Container, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { CHAT_API, TUTOR_STUDENT_DOUBTS_API } from "../../../api/api";

import { AiOutlineRise } from "react-icons/ai";
import { IoSend } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { CgAttachment } from "react-icons/cg";
import { FaSearch } from "react-icons/fa";
import { HiPencil } from "react-icons/hi";

import PLACEHOLDER_IMG from "../../../assets/no-queries-found.png";

// import Noofstudents from "../../../assets/tutor-student-dou-icon1.png";
// import TotalQueries from "../../../assets/tutor-student-dou-icon2.png";
// import Unresolved from "../../../assets/all-assignments-icon.png";
// import Resolved from "../../../assets/tutor-student-dou-icon4.png";
// import ResponseRate from "../../../assets/tutor-student-dou-icon5.png";

// latestIcons
// import noOfStudents from "../../../assets/icons/tutor/active-students.svg";
import enrolledStudents from "../../../assets/icons/admin/enrolled-students.svg";
import totalQueries from "../../../assets/icons/tutor/total-queries.svg";
import unresolved from "../../../assets/icons/tutor/unresolved.svg";
import resolved from "../../../assets/icons/admin/avg-final-assig-comp.svg";
import responseRate from "../../../assets/icons/tutor/response-rate.svg";

import albumImage from "../../../assets/icons/student/album.svg";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./TutorStudentDoubts.css";

const TutorStudentDoubts = () => {
  const navigate = useNavigate();

  const [tutorChatStats, setTutorChatStats] = useState({});
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [messages, setMessages] = useState([]);
  const [pauseChat, setPauseChat] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");

  // search states
  const [searchTerm, setSearchTerm] = useState("");

  //loading
  const [loadingAllChats, setloadingAllChats] = useState(true);
  const [loadingSendMsg, setloadingSendMsg] = useState(false);

  const fileInputRef = useRef();
  const messagesEndRef = useRef();
  const currentUserRole = "tutor";

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileData = {
      file, // <-- raw File object from input
      preview: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    };

    setSelectedFile(fileData);
    // Clear text input if a file is selected
    setMessageText("");
  };

  // Cleanup object URL (memory leak fix)
  useEffect(() => {
    return () => {
      if (selectedFile?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedFile.preview);
      }
    };
  }, [selectedFile]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingAllChats(false);
      navigate("/signin");

      return;
    }

    const fetchTutorChatStats = async () => {
      try {
        const response = await axios.post(
          TUTOR_STUDENT_DOUBTS_API.CHAT_STATS,
          { tutor_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setTutorChatStats(data.stats || {});
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setloadingAllChats(false);
      }
    };

    fetchTutorChatStats();
  }, [navigate]);

  const fetchStudents = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingAllChats(false);
      navigate("/signin");

      return;
    }
    try {
      const response = await axios.post(
        CHAT_API.GET_CHAT_LIST,
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setStudents(data.chatList || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingAllChats(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Click student → fetch messages
  const handleStudentClick = async (student) => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");

      return;
    }

    setSelectedStudent(student);

    // Clear input & file when switching tutor
    setMessageText("");
    setSelectedFile(null);
    setEditingMessageId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!student.chat_room_id) {
      // No chat room yet → empty messages
      setMessages([]);
      setPauseChat(false);
      return;
    }

    try {
      const response = await axios.post(
        CHAT_API.GET_MESSAGES,
        { user_id: userId, chat_room_id: student.chat_room_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        const getFileType = (fileName = "") => {
          const ext = fileName.split(".").pop()?.toLowerCase();

          if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
            return "image";
          }

          return "file";
        };

        const formattedMessages = (data.messages || []).map((msg) => ({
          chatId: msg.chat_id,
          fromRole: msg.role?.trim().toLowerCase(),
          text: msg.message,
          time: msg.to_char,
          edited: msg.edited,
          file: msg.file_url
            ? {
                preview: msg.file_url,
                name: msg.file
                  ? msg.file.split("/").pop().replace(/^\d+_/, "")
                  : "File",
                type: getFileType(msg.file),
              }
            : null,
        }));

        setMessages(formattedMessages);
        setPauseChat(Boolean(data.pause_chat)); // true or false from API

        await fetchStudents();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Msg error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  // Scroll to bottom on messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleTutorSendMsg = async () => {
    if (!selectedStudent) {
      showErrorToast("Please select a conversation first.");
      return;
    }

    if (!messageText.trim() && !selectedFile) return;

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingSendMsg(false);
      navigate("/signin");

      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("student_id", selectedStudent.student_id);
    formData.append("course_id", selectedStudent.course_id);
    formData.append("message", messageText);

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file);
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.SEND_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        // If chat_room_id was null → refresh page
        if (!selectedStudent.chat_room_id) {
          window.location.reload();
          return;
        } else await handleStudentClick(selectedStudent);

        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error send message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  const handleShowEditMsg = (msg) => {
    setMessageText(msg.text || "");
    setSelectedFile(msg.file || null);
    setEditingMessageId(msg.chatId);

    setTimeout(() => {
      document.querySelector(".tutor-doubts-input")?.focus();
    }, 0);
  };

  // Edit message
  const handleUpdateTutorSendMsg = async () => {
    if (!editingMessageId) {
      showErrorToast("No message selected to edit.");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setloadingSendMsg(false);
      navigate("/signin");

      return;
    }

    const formData = new FormData();
    formData.append("chat_id", editingMessageId);
    formData.append("message", messageText);

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file); // key matches backend
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.UPDATE_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        await handleStudentClick(selectedStudent);

        setEditingMessageId(null);
        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error update message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  const filteredCoursesForDropdown = useMemo(
    () => [...new Set(students.map((s) => s.course_title))],
    [students],
  );

  // STUDENTS DEPENDING ON COURSE
  const filteredStudentsForDropdown = useMemo(() => {
    return selectedCourseFilter === "All Courses"
      ? [...new Set(students.map((s) => s.student_name))]
      : [
          ...new Set(
            students
              .filter((s) => s.course_title === selectedCourseFilter)
              .map((s) => s.student_name),
          ),
        ];
  }, [students, selectedCourseFilter]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  // Filter students based on course, and search term
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (
        selectedCourseFilter !== "All Courses" &&
        s.course_title !== selectedCourseFilter
      )
        return false;
      if (
        selectedStudentFilter !== "All Students" &&
        s.student_name !== selectedStudentFilter
      )
        return false;
      if (
        normalizedSearch &&
        !(
          s.student_name.toLowerCase().includes(normalizedSearch) ||
          s.course_title.toLowerCase().includes(normalizedSearch)
        )
      )
        return false;
      return true;
    });
  }, [students, selectedCourseFilter, selectedStudentFilter, normalizedSearch]);

  if (loadingAllChats) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="tutor-student-doubts-root">
      <h4 className="tutor-feedback-title">Student Doubts</h4>
      <p className="tutor-feedback-subtitle">
        Respond to student queries and resolve doubts.
      </p>

      <Row className="tutor-student-doubts-top-cards-row">
        <Col lg={2} md={4} sm={6} xs={12}>
          <div className="tutor-student-doubts-top-info-card">
            <h6>Enrolled Students</h6>
            <img
              src={enrolledStudents}
              alt="No of students"
              className="tutor-student-doubts-top-info-card-icon"
            />
            <h3>{Number(tutorChatStats.total_students) || "-"}</h3>
            <span className="tutor-student-doubts-top-card-link-green">
              <AiOutlineRise /> +12% vs last week
            </span>
          </div>
        </Col>

        <Col lg={2} md={4} sm={6} xs={12}>
          <div className="tutor-student-doubts-top-info-card">
            <h6>Total Queries</h6>
            <img
              src={totalQueries}
              alt="Total Queries"
              className="tutor-student-doubts-top-info-card-icon"
            />
            <h3>{Number(tutorChatStats.total_queries) || "-"}</h3>
            <span className="tutor-student-doubts-top-card-link-red">
              -5% vs yesterday
            </span>
          </div>
        </Col>

        <Col lg={2} md={4} sm={6} xs={12}>
          <div className="tutor-student-doubts-top-info-card">
            <h6>Unresolved</h6>
            <img
              src={unresolved}
              alt="Unresolved"
              className="tutor-student-doubts-top-info-card-icon"
            />
            <h3>{Number(tutorChatStats.unresolved) || "-"}</h3>
            <span className="tutor-student-doubts-top-card-link-yellow">
              Requires evaluation
            </span>
          </div>
        </Col>

        <Col lg={2} md={4} sm={6} xs={12}>
          <div className="tutor-student-doubts-top-info-card">
            <h6>Resolved</h6>
            <img
              src={resolved}
              alt="Resolved"
              className="tutor-student-doubts-top-info-card-icon"
            />
            <h3>{Number(tutorChatStats.resolved) || "-"}</h3>
            <span className="tutor-student-doubts-top-card-link-green">
              +12% vs last week
            </span>
          </div>
        </Col>

        <Col lg={2} md={4} sm={6} xs={12}>
          <div className="tutor-student-doubts-top-info-card">
            <h6>Response Rate</h6>
            <img
              src={responseRate}
              alt="Response Rate"
              className="tutor-student-doubts-top-info-card-icon"
            />
            <h3>{Number(tutorChatStats.response_rate) || "-"}</h3>
            <span className="tutor-student-doubts-top-card-link-green">
              +12% vs last week
            </span>
          </div>
        </Col>
      </Row>

      <div className="tutor-doubts-root-section-divssss mb-4">
        <Row className="tutor-doubts-totalboxss">
          {/* Left: Student List & Filters */}
          <Col md={4} className="tutor-doubts-left">
            <div className="tutor-doubts-filters-wrapper mb-3 d-flex gap-2">
              {/* Course Filter */}
              <Form.Select
                value={selectedCourseFilter}
                onChange={(e) => setSelectedCourseFilter(e.target.value)}
                className="tutor-doubts-filter-select"
              >
                <option value="All Courses">All Courses</option>
                {filteredCoursesForDropdown.map((course, idx) => (
                  <option key={idx} value={course}>
                    {course}
                  </option>
                ))}
              </Form.Select>

              {/* student Filter */}
              <Form.Select
                value={selectedStudentFilter}
                onChange={(e) => setSelectedStudentFilter(e.target.value)}
                className="tutor-doubts-filter-select"
              >
                <option value="All Students">All Students</option>
                {filteredStudentsForDropdown.map((student, idx) => (
                  <option key={idx} value={student}>
                    {student}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Search */}
            <div className="tutor-doubts-search-wrap mb-3">
              <input
                type="text"
                placeholder="Search by student name or course"
                className="tutor-doubts-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="tutor-doubts-search-icon">
                <FaSearch />
              </span>
            </div>

            {/* Student List */}
            <div className="tutor-doubts-list">
              {filteredStudents.length ? (
                filteredStudents.map((s) => (
                  <div
                    key={`${s.student_id}-${s.course_id}`}
                    className={`tutor-doubts-list-item ${
                      selectedStudent?.student_id === s.student_id &&
                      selectedStudent?.course_id === s.course_id
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleStudentClick(s)}
                  >
                    <div className="tutor-doubts-initials">
                      {initialsFromName(s.student_name)}
                    </div>

                    <div className="tutor-doubts-meta">
                      <div className="tutor-doubts-meta-top">
                        <span
                          className="tutor-doubts-name"
                          title={s.student_name}
                        >
                          {s.student_name}
                          <span
                            className="tutor-doubts-course-badge"
                            title={s.course_title}
                          >
                            ({s.course_title})
                          </span>
                        </span>

                        {/* <span className="tutor-doubts-time">{s.lastUpdated}</span> */}
                      </div>

                      <div
                        className="tutor-doubts-snippet"
                        // title={s.last_message}
                      >
                        {s.last_message}
                      </div>
                    </div>

                    {Number(s.unseen_count) > 0 && (
                      <div className="tutor-doubts-unread">
                        {s.unseen_count}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="tutor-admin-doubts-empty text-center p-4 mt-5">
                  <img
                    src={PLACEHOLDER_IMG}
                    alt="no queries"
                    className="tutor-admin-doubts-img-fluidsss"
                  />
                  <p className="mt-3 tutor-admin-doubts-img-bottom-text">
                    No results found
                  </p>
                  <p className="mt-0 tutor-admin-doubts-img-bottom-para">
                    Try searching with a different name or keyword
                  </p>
                </div>
              )}
            </div>
          </Col>

          {/* Right: Chat Area */}
          <Col md={8} className="tutor-doubts-right p-0">
            {selectedStudent ? (
              <>
                <div className="tutor-doubts-right-header">
                  <div className="d-flex align-items-center gap-3">
                    <div className="tutor-doubts-header-initials">
                      {initialsFromName(selectedStudent.student_name)}
                    </div>

                    <div>
                      <div className="tutor-doubts-header-name">
                        {selectedStudent.student_name}
                      </div>
                      <div className="tutor-doubts-header-role">
                        {selectedStudent.course_title}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="tutor-doubts-chat-area">
                  {messages?.map((m) => (
                    <div
                      key={m.chatId}
                      className={`d-flex flex-column mb-3 ${
                        m.fromRole === currentUserRole
                          ? "align-items-end"
                          : "align-items-start"
                      }`}
                    >
                      {m.edited && (
                        <div className="student-doubts-edited-label">
                          Edited
                        </div>
                      )}
                      <div className={`tutor-doubts-msg ${m.fromRole}`}>
                        <div className="tutor-doubts-msg-text">
                          {m.text && <div>{m.text}</div>}

                          {m.file && (
                            <>
                              {m.file?.type === "image" ? (
                                <a
                                  href={m.file.preview}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    src={m.file.preview}
                                    alt="sent-img"
                                    style={{
                                      maxWidth: "200px",
                                      borderRadius: "8px",
                                      marginTop: "5px",
                                    }}
                                  />
                                </a>
                              ) : (
                                <div className="file-box">
                                  <a
                                    href={m.file.preview}
                                    download={m.file.name}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <CgAttachment /> {m.file.name}
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* FOOTER */}
                        <div className="tutor-doubts-msg-footer">
                          <span className="msg-role-label">
                            {m.fromRole === "tutor"
                              ? "Tutor"
                              : m.fromRole === "student"
                                ? "By Student"
                                : "By Admin"}
                          </span>

                          <div className="tutor-doubts-msg-footer-twosss">
                            <span className="tutor-doubts-msg-time">
                              {m.time}
                            </span>

                            {m.fromRole === "tutor" && (
                              <HiPencil
                                className="msg-icon"
                                onClick={() => handleShowEditMsg(m)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Bar */}
                {pauseChat ? (
                  <div className="admin-chat-paused-box">
                    ⚠ Chat has been paused by admin. Messaging is disabled.
                  </div>
                ) : (
                  <div className="tutor-doubts-input-bar">
                    <div className="tutor-doubts-input-inner">
                      {selectedFile && (
                        <div className="inline-file-preview">
                          <span className="file-name" title={selectedFile.name}>
                            <CgAttachment /> {selectedFile.name}
                          </span>

                          <span
                            className="remove-file"
                            onClick={() => {
                              setSelectedFile(null);
                              setMessageText("");
                              if (fileInputRef.current)
                                fileInputRef.current.value = "";
                            }}
                            title="Remove file"
                          >
                            <RxCross2 />
                          </span>
                        </div>
                      )}

                      <Form.Control
                        className="tutor-doubts-input"
                        placeholder={
                          editingMessageId
                            ? "Edit your message..."
                            : selectedFile
                              ? "File selected — cannot type message"
                              : "Type a message..."
                        }
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            editingMessageId
                              ? handleUpdateTutorSendMsg()
                              : handleTutorSendMsg();
                          }
                        }}
                        autoComplete="off"
                        disabled={!!selectedFile}
                      />

                      <img
                        src={albumImage}
                        alt="attach"
                        className="tutor-doubts-attach-icon"
                        onClick={() => fileInputRef.current.click()}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleAttach}
                      />
                    </div>

                    <Button
                      className="tutor-doubts-send-btn"
                      onClick={() =>
                        editingMessageId
                          ? handleUpdateTutorSendMsg()
                          : handleTutorSendMsg()
                      }
                      disabled={loadingSendMsg}
                    >
                      {loadingSendMsg ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <IoSend />
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="tutor-admin-doubts-empty text-center p-4 mt-5">
                <img
                  src={PLACEHOLDER_IMG}
                  alt="no queries"
                  className="tutor-admin-doubts-img-fluidsss"
                />
                <p className="mt-3 tutor-admin-doubts-img-bottom-text">
                  Select a conversation.
                </p>
                <p className="mt-0 tutor-admin-doubts-img-bottom-para">
                  Choose a conversation to view messages and respond.
                </p>
              </div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default TutorStudentDoubts;
